import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { fetchUserDetails } from '../store/feature/auth/authThunk';
import { canAccessRoute, getClosestAllowedRoute } from '../utils/permissionService';
import Loading from 'src/views/aimhi-widgets/Loading';
import { trackEvent, mixPanelEvents } from 'src/utils/mixpanel';
import routes from "src/routes";
import { matchPath } from "react-router-dom";

const RouteGuard = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [isNavigationAllowed, setIsNavigationAllowed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const status = useSelector(state => state.auth.authStatus);
  const token = useSelector(state => state.auth.token);
  const allowedPermission = useSelector(state => state.auth.accessPermissions);
  const userRoleID = useSelector(state => state.auth.userRole?.role_id);


  const beforeEachNavigation = (to) => {
    setIsLoading(true);
    try {
      let pathName;

      const hasRedirectParam = to.pathname === '/' && to.search.startsWith('?redirect=');
      if (hasRedirectParam) {
        const params = new URLSearchParams(to.search);
        const redirectPath = params.get('redirect');

        if (token && redirectPath) {
          history.replace(redirectPath);
          setIsLoading(false);
          return;
        }
        pathName = '/';
      } else {
        pathName = to.pathname;
      }
      const { allowed, type } = canAccessRoute(pathName, allowedPermission, token, userRoleID);
      const isFetching = status === 'loading';
      const noPermissions = allowedPermission.length === 0;
      const isPublic = type === 'PUBLIC';
      const isPassable = type === 'PASSABLE';

      if (isPublic) {
        setIsNavigationAllowed(true);
        setIsLoading(false);
        return;
      }

      if (!token) {
        history.replace('/');
        setIsLoading(false);
        return;
      }

      if (isPassable) {
        if (status === 'idle' || status === 'failed') {
          dispatch(fetchUserDetails());
          return;
        }
        setIsNavigationAllowed(true);
        setIsLoading(false);
        return;
      }

      if (status === 'idle') {
        dispatch(fetchUserDetails());
        return;
      }

      if (isFetching && noPermissions) {
        return;
      }

      if (status === 'succeeded' && !allowed) {
        setIsLoading(false);
        setIsNavigationAllowed(true);
        history.replace('/403');
        return;
      }

      if (allowed) {
        setIsNavigationAllowed(true);
        setIsLoading(false);
        return;
      }

      const fallback = getClosestAllowedRoute(allowedPermission, userRoleID);
      if (fallback) {
        history.replace(fallback);
      } else {
        setIsLoading(false);
        setIsNavigationAllowed(true);
        history.replace('/403');
      }
      setIsLoading(false);

    }
    catch (error) {
      console.error('Navigation guard error:', error);
      history.replace('/404');
      setIsLoading(false);
    }
  };
  const useTrackNavigation = (location) => {
    const prevPathnameRef = useRef(null);
    const firstRenderRef = useRef(true);

    const matchedRoute = routes.find(route =>
      matchPath(location.pathname, { path: route.path, exact: route.exact ?? true })
    );

    const checkAndTrack = () => {
      const currentPathname = matchedRoute?.path;
      if (firstRenderRef.current) {
        firstRenderRef.current = false;
        prevPathnameRef.current = currentPathname;
        return;
      }
      if (currentPathname === "/") {
        prevPathnameRef.current = currentPathname;
        return;
      }
      if (prevPathnameRef.current !== currentPathname) {
        prevPathnameRef.current = currentPathname;
      }
    };

    return checkAndTrack;
  };


  const trackNavigation = useTrackNavigation(location);

  useEffect(() => {
    beforeEachNavigation(location);
    trackNavigation();
  }, [location.pathname, token, status, allowedPermission, userRoleID]);

  const { allowed, type } = canAccessRoute(location.pathname, allowedPermission, token, userRoleID);

  const shouldShowLoading = (type !== 'PUBLIC' && !allowed) && (isLoading || (token && (status === 'loading' || allowedPermission.length === 0)));

  if (shouldShowLoading || (!isNavigationAllowed && !allowed && type !== 'PUBLIC')) {
    return <Loading />;
  }

  return children;
};

export default RouteGuard;
